import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { 
  Film, ArrowLeft, Users, Plus, Trash2, Edit, Mail, Shield, 
  UserCheck, User, Loader2, Search, MoreVertical, RefreshCw, LogIn,
  BarChart3, Zap, Crown, Sparkles, CreditCard, Minus, DollarSign,
  Tag, Calendar, AlertCircle, Receipt
} from "lucide-react";
import UsageReport from "@/components/UsageReport";

import { getApiUrl } from '@/config/api';

const API = getApiUrl();

// Plan display configuration
const PLAN_DISPLAY = {
  ambassador: { name: "Ambassador", color: "bg-zinc-500/20 text-zinc-400 border-zinc-500/30", icon: User },
  apprentice: { name: "Apprentice", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", icon: Sparkles },
  director: { name: "Director", color: "bg-purple-500/20 text-purple-400 border-purple-500/30", icon: Crown },
  studio: { name: "Studio", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", icon: Crown },
};

export default function SuperAdmin() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, token, getAuthHeaders, isSuperAdmin, impersonateUser, isImpersonating } = useAuth();
  const [users, setUsers] = useState([]);
  const [userPlans, setUserPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [impersonatingUserId, setImpersonatingUserId] = useState(null);
  const [upgradeForm, setUpgradeForm] = useState({ plan: "apprentice", reason: "Beta tester upgrade" });
  
  // Billing management state
  const [grantSparksDialogOpen, setGrantSparksDialogOpen] = useState(false);
  const [deductSparksDialogOpen, setDeductSparksDialogOpen] = useState(false);
  const [userDetailsDialogOpen, setUserDetailsDialogOpen] = useState(false);
  const [couponDialogOpen, setCouponDialogOpen] = useState(false);
  const [sparkAmount, setSparkAmount] = useState(1000);
  const [sparkReason, setSparkReason] = useState("");
  const [userBillingDetails, setUserBillingDetails] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [loadingCoupons, setLoadingCoupons] = useState(false);
  const [couponForm, setCouponForm] = useState({
    name: "",
    discount_type: "percent",
    discount_value: 20,
    duration: "once",
    duration_in_months: 3,
    max_redemptions: 100,
    code: ""
  });
  
  // Tab state from URL
  const activeTab = searchParams.get("tab") || "users";
  const setActiveTab = (tab) => setSearchParams({ tab });
  
  const [newUser, setNewUser] = useState({
    email: "",
    password: "",
    name: "",
    role: "director"
  });

  useEffect(() => {
    if (!isSuperAdmin) {
      toast.error("Access denied. Super Admin only.");
      navigate("/");
      return;
    }
    fetchUsers();
    fetchUserPlans();
  }, [isSuperAdmin, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data.users || []);
    } catch (error) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserPlans = async () => {
    try {
      const response = await axios.get(`${API}/billing/admin/all-users-sparks`, {
        headers: getAuthHeaders()
      });
      // Convert to map for easy lookup
      const plansMap = {};
      (response.data.users || []).forEach(u => {
        plansMap[u.user_id] = {
          plan: u.subscription_plan || "ambassador",
          spark_balance: u.spark_balance || 0
        };
      });
      setUserPlans(plansMap);
    } catch (error) {
      console.error("Failed to load user plans:", error);
    }
  };

  const handleUpgradeUser = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/billing/admin/upgrade-user-plan`,
        null,
        {
          params: {
            target_user_id: selectedUser.id,
            plan: upgradeForm.plan,
            reason: upgradeForm.reason
          },
          headers: getAuthHeaders()
        }
      );
      toast.success(`Upgraded ${selectedUser.name || selectedUser.email} to ${upgradeForm.plan} plan!`);
      setUpgradeDialogOpen(false);
      setSelectedUser(null);
      fetchUserPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upgrade user");
    } finally {
      setSaving(false);
    }
  };

  const handleGrantSparks = async () => {
    if (!selectedUser || sparkAmount <= 0) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/billing/admin/grant-sparks`,
        null,
        {
          params: {
            target_user_id: selectedUser.id,
            sparks: sparkAmount,
            reason: sparkReason || "Admin grant"
          },
          headers: getAuthHeaders()
        }
      );
      toast.success(`Granted ${sparkAmount.toLocaleString()} sparks to ${selectedUser.name || selectedUser.email}`);
      setGrantSparksDialogOpen(false);
      setSelectedUser(null);
      setSparkAmount(1000);
      setSparkReason("");
      fetchUserPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to grant sparks");
    } finally {
      setSaving(false);
    }
  };

  const handleDeductSparks = async () => {
    if (!selectedUser || sparkAmount <= 0) return;
    setSaving(true);
    try {
      await axios.post(
        `${API}/billing/admin/deduct-sparks`,
        null,
        {
          params: {
            target_user_id: selectedUser.id,
            sparks: sparkAmount,
            reason: sparkReason || "Admin deduction/refund"
          },
          headers: getAuthHeaders()
        }
      );
      toast.success(`Deducted ${sparkAmount.toLocaleString()} sparks from ${selectedUser.name || selectedUser.email}`);
      setDeductSparksDialogOpen(false);
      setSelectedUser(null);
      setSparkAmount(1000);
      setSparkReason("");
      fetchUserPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deduct sparks");
    } finally {
      setSaving(false);
    }
  };

  const fetchUserBillingDetails = async (userId) => {
    try {
      const response = await axios.get(
        `${API}/billing/admin/user-plan/${userId}`,
        { headers: getAuthHeaders() }
      );
      setUserBillingDetails(response.data);
      setUserDetailsDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load user billing details");
    }
  };

  const fetchCoupons = async () => {
    setLoadingCoupons(true);
    try {
      const response = await axios.get(
        `${API}/billing/admin/coupons`,
        { headers: getAuthHeaders() }
      );
      setCoupons(response.data.coupons || []);
    } catch (error) {
      toast.error("Failed to load coupons");
    } finally {
      setLoadingCoupons(false);
    }
  };

  const handleCreateCoupon = async () => {
    if (!couponForm.name || !couponForm.code) {
      toast.error("Name and code are required");
      return;
    }
    setSaving(true);
    try {
      await axios.post(
        `${API}/billing/admin/coupon`,
        couponForm,
        { headers: getAuthHeaders() }
      );
      toast.success(`Coupon "${couponForm.code}" created successfully!`);
      setCouponDialogOpen(false);
      setCouponForm({
        name: "",
        discount_type: "percent",
        discount_value: 20,
        duration: "once",
        duration_in_months: 3,
        max_redemptions: 100,
        code: ""
      });
      fetchCoupons();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create coupon");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;
    try {
      await axios.delete(
        `${API}/billing/admin/coupon/${couponId}`,
        { headers: getAuthHeaders() }
      );
      toast.success("Coupon deleted");
      fetchCoupons();
    } catch (error) {
      toast.error("Failed to delete coupon");
    }
  };

  const getPlanBadge = (userId) => {
    const userPlan = userPlans[userId];
    const plan = userPlan?.plan || "ambassador";
    const config = PLAN_DISPLAY[plan] || PLAN_DISPLAY.ambassador;
    const PlanIcon = config.icon;
    return (
      <Badge variant="outline" className={config.color}>
        <PlanIcon className="w-3 h-3 mr-1" />
        {config.name}
      </Badge>
    );
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) {
      toast.error("Email and password are required");
      return;
    }
    setSaving(true);
    try {
      const response = await axios.post(`${API}/admin/users`, newUser, {
        headers: getAuthHeaders()
      });
      setUsers([...users, response.data.user]);
      setNewUser({ email: "", password: "", name: "", role: "director" });
      setCreateDialogOpen(false);
      toast.success("User created successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    setSaving(true);
    try {
      const response = await axios.put(
        `${API}/admin/users/${selectedUser.id}`,
        {
          name: selectedUser.name,
          role: selectedUser.role,
          is_active: selectedUser.is_active
        },
        { headers: getAuthHeaders() }
      );
      setUsers(users.map(u => u.id === selectedUser.id ? response.data.user : u));
      setEditDialogOpen(false);
      setSelectedUser(null);
      toast.success("User updated successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, {
        headers: getAuthHeaders()
      });
      setUsers(users.filter(u => u.id !== userId));
      toast.success("User deleted");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleResendInvite = async (actorId) => {
    try {
      await axios.post(`${API}/director/resend-invite`, {
        actor_id: actorId
      }, { headers: getAuthHeaders() });
      toast.success("Email sent successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  const handleImpersonate = async (targetUser) => {
    if (targetUser.id === user?.id) {
      toast.error("Cannot impersonate yourself");
      return;
    }
    
    setImpersonatingUserId(targetUser.id);
    const result = await impersonateUser(targetUser.id);
    setImpersonatingUserId(null);
    
    if (result.success) {
      toast.success(`Now viewing as ${targetUser.name || targetUser.email}`);
      // Navigate to the appropriate page based on user role
      if (targetUser.role === 'actor') {
        navigate('/actor-portal');
      } else {
        navigate('/');
      }
    } else {
      toast.error(result.error || "Failed to impersonate user");
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      super_admin: "bg-red-500/20 text-red-400 border-red-500/30",
      director: "bg-indigo-500/20 text-indigo-400 border-indigo-500/30",
      actor: "bg-amber-500/20 text-amber-400 border-amber-500/30"
    };
    const labels = {
      super_admin: "Super Admin",
      director: "Director",
      actor: "Actor"
    };
    return (
      <Badge variant="outline" className={styles[role] || styles.director}>
        {labels[role] || role}
      </Badge>
    );
  };

  const filteredUsers = users.filter(u => 
    u.email.toLowerCase().includes(search.toLowerCase()) ||
    (u.name && u.name.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-[#09090b] p-4 md:p-8 animate-fadeIn">
      {/* Header */}
      <header className="max-w-6xl mx-auto mb-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate("/")}
              className="text-zinc-400 hover:text-white"
              data-testid="super-admin-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <Shield className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">Super Admin</h1>
                <p className="text-sm text-zinc-500">User Management</p>
              </div>
            </div>
          </div>

          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-user-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Email *</Label>
                  <Input
                    type="email"
                    placeholder="user@example.com"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="new-user-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Password *</Label>
                  <Input
                    type="password"
                    placeholder="••••••••"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="new-user-password"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Name</Label>
                  <Input
                    placeholder="John Doe"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    data-testid="new-user-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-zinc-300">Role</Label>
                  <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="new-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="director">Director</SelectItem>
                      <SelectItem value="actor">Actor</SelectItem>
                      <SelectItem value="super_admin">Super Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                    Cancel
                  </Button>
                </DialogClose>
                <Button 
                  onClick={handleCreateUser} 
                  className="bg-indigo-600 hover:bg-indigo-700"
                  disabled={saving}
                  data-testid="create-user-submit"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Create User
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {/* Tabs */}
      <div className="max-w-6xl mx-auto">
        <Tabs value={activeTab} onValueChange={(tab) => {
          setActiveTab(tab);
          if (tab === "billing") fetchCoupons();
        }}>
          <TabsList className="bg-zinc-900 border-zinc-800 mb-6">
            <TabsTrigger 
              value="users" 
              className="data-[state=active]:bg-zinc-800"
              data-testid="users-tab"
            >
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger 
              value="billing" 
              className="data-[state=active]:bg-zinc-800"
              data-testid="billing-tab"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Billing & Coupons
            </TabsTrigger>
            <TabsTrigger 
              value="usage" 
              className="data-[state=active]:bg-zinc-800"
              data-testid="usage-tab"
            >
              <BarChart3 className="w-4 h-4 mr-2" />
              Usage & Costs
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            {/* Search */}
            <div className="mb-6">
              <div className="relative max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-zinc-900 border-zinc-800 text-white placeholder:text-zinc-500"
                  data-testid="user-search-input"
                />
              </div>
            </div>

            {/* Users List */}
            {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
          </div>
        ) : filteredUsers.length === 0 ? (
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400">
                {search ? "No users found matching your search" : "No users yet"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {filteredUsers.map((u) => (
              <Card key={u.id} className="bg-zinc-900/50 border-zinc-800 hover:border-zinc-700 transition-colors" data-testid={`user-card-${u.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                        {u.role === "super_admin" ? (
                          <Shield className="w-5 h-5 text-red-400" />
                        ) : u.role === "director" ? (
                          <UserCheck className="w-5 h-5 text-indigo-400" />
                        ) : (
                          <User className="w-5 h-5 text-amber-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-white">{u.name || u.email}</h3>
                          {getRoleBadge(u.role)}
                          {getPlanBadge(u.id)}
                          {!u.is_active && (
                            <Badge variant="outline" className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30">
                              Inactive
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-zinc-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {u.email}
                          {userPlans[u.id]?.spark_balance >= 0 && (
                            <span className="ml-3 text-indigo-400 cursor-pointer hover:text-indigo-300" onClick={() => fetchUserBillingDetails(u.id)}>
                              <Zap className="w-3 h-3 inline mr-0.5" />
                              {userPlans[u.id].spark_balance.toLocaleString()} sparks
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Grant Sparks button */}
                      {u.id !== user?.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(u);
                            setSparkAmount(1000);
                            setSparkReason("");
                            setGrantSparksDialogOpen(true);
                          }}
                          className="text-green-400 border-green-500/30 hover:bg-green-500/10"
                          data-testid={`grant-sparks-btn-${u.id}`}
                        >
                          <Plus className="w-4 h-4 mr-1" />
                          Grant
                        </Button>
                      )}
                      {/* Deduct Sparks button */}
                      {u.id !== user?.id && userPlans[u.id]?.spark_balance > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(u);
                            setSparkAmount(Math.min(1000, userPlans[u.id].spark_balance));
                            setSparkReason("");
                            setDeductSparksDialogOpen(true);
                          }}
                          className="text-red-400 border-red-500/30 hover:bg-red-500/10"
                          data-testid={`deduct-sparks-btn-${u.id}`}
                        >
                          <Minus className="w-4 h-4 mr-1" />
                          Deduct
                        </Button>
                      )}
                      {/* Upgrade Plan button */}
                      {u.id !== user?.id && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedUser(u);
                            setUpgradeForm({ plan: userPlans[u.id]?.plan || "apprentice", reason: "Beta tester upgrade" });
                            setUpgradeDialogOpen(true);
                          }}
                          className="text-purple-400 border-purple-500/30 hover:bg-purple-500/10"
                          data-testid={`upgrade-plan-btn-${u.id}`}
                        >
                          <Crown className="w-4 h-4 mr-1" />
                          Upgrade
                        </Button>
                      )}
                      {/* Login as User button - only for non-super-admin users */}
                      {u.id !== user?.id && !isImpersonating && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleImpersonate(u)}
                          className="text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10"
                          title={`Login as ${u.name || u.email}`}
                          disabled={impersonatingUserId === u.id}
                          data-testid={`impersonate-user-${u.id}`}
                        >
                          {impersonatingUserId === u.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <LogIn className="w-4 h-4" />
                          )}
                        </Button>
                      )}
                      {u.role === "actor" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleResendInvite(u.id)}
                          className="text-zinc-400 hover:text-white"
                          title="Resend invite/login email"
                          data-testid={`resend-invite-${u.id}`}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedUser({ ...u });
                          setEditDialogOpen(true);
                        }}
                        className="text-zinc-400 hover:text-white"
                        data-testid={`edit-user-${u.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {u.role !== "super_admin" && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteUser(u.id)}
                          className="text-zinc-400 hover:text-red-400"
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
          </TabsContent>

          {/* Billing & Coupons Tab */}
          <TabsContent value="billing">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Coupon Management */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-white flex items-center gap-2">
                      <Tag className="w-5 h-5 text-indigo-400" />
                      Coupon Codes
                    </CardTitle>
                    <Button
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700"
                      onClick={() => setCouponDialogOpen(true)}
                      data-testid="create-coupon-btn"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Create Coupon
                    </Button>
                  </div>
                  <CardDescription className="text-zinc-400">
                    Manage discount codes for subscriptions
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {loadingCoupons ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
                    </div>
                  ) : coupons.length === 0 ? (
                    <div className="text-center py-8 text-zinc-500">
                      <Tag className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      <p>No coupons created yet</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {coupons.map((coupon) => (
                        <div
                          key={coupon.id}
                          className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-lg"
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-white font-medium">{coupon.name}</span>
                              <Badge variant="outline" className="border-indigo-500/30 text-indigo-400">
                                {coupon.percent_off ? `${coupon.percent_off}% off` : `$${coupon.amount_off} off`}
                              </Badge>
                            </div>
                            <p className="text-zinc-500 text-xs mt-1">
                              {coupon.duration === "once" ? "One-time" : 
                               coupon.duration === "forever" ? "Forever" : 
                               `${coupon.duration_in_months} months`}
                              {coupon.max_redemptions && ` • Max ${coupon.max_redemptions} uses`}
                              {coupon.times_redeemed > 0 && ` • Used ${coupon.times_redeemed}x`}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteCoupon(coupon.id)}
                            className="text-zinc-400 hover:text-red-400"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Actions */}
              <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Zap className="w-5 h-5 text-amber-400" />
                    Quick Actions
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Manage user sparks and billing
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <h4 className="text-white font-medium mb-2">Grant/Deduct Sparks</h4>
                    <p className="text-zinc-500 text-sm mb-3">
                      Select a user from the Users tab to grant or deduct sparks from their account.
                    </p>
                    <Button
                      variant="outline"
                      className="border-zinc-700"
                      onClick={() => setActiveTab("users")}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Go to Users
                    </Button>
                  </div>
                  
                  <div className="bg-zinc-800/50 rounded-lg p-4">
                    <h4 className="text-white font-medium mb-2">Stripe Dashboard</h4>
                    <p className="text-zinc-500 text-sm mb-3">
                      For refunds and detailed billing management, use the Stripe dashboard.
                    </p>
                    <Button
                      variant="outline"
                      className="border-zinc-700"
                      onClick={() => window.open("https://dashboard.stripe.com", "_blank")}
                    >
                      <DollarSign className="w-4 h-4 mr-2" />
                      Open Stripe
                    </Button>
                  </div>
                  
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                      <div>
                        <h4 className="text-amber-400 font-medium">Refund Process</h4>
                        <p className="text-zinc-400 text-sm mt-1">
                          To refund a user: 1) Deduct sparks from their account, 2) Process the refund in Stripe dashboard.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Usage & Costs Tab */}
          <TabsContent value="usage">
            <UsageReport />
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Email</Label>
                <Input
                  value={selectedUser.email}
                  disabled
                  className="bg-zinc-800 border-zinc-700 text-zinc-500"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Name</Label>
                <Input
                  value={selectedUser.name || ""}
                  onChange={(e) => setSelectedUser({ ...selectedUser, name: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  data-testid="edit-user-name"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Role</Label>
                <Select 
                  value={selectedUser.role} 
                  onValueChange={(v) => setSelectedUser({ ...selectedUser, role: v })}
                  disabled={selectedUser.role === "super_admin"}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="director">Director</SelectItem>
                    <SelectItem value="actor">Actor</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={selectedUser.is_active}
                  onChange={(e) => setSelectedUser({ ...selectedUser, is_active: e.target.checked })}
                  className="rounded border-zinc-700 bg-zinc-800"
                  data-testid="edit-user-active"
                />
                <Label htmlFor="is_active" className="text-zinc-300">Active</Label>
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleUpdateUser} 
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={saving}
              data-testid="edit-user-submit"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upgrade Plan Dialog */}
      <Dialog open={upgradeDialogOpen} onOpenChange={setUpgradeDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-purple-400" />
              Upgrade User Plan
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <div>
                <p className="text-sm text-zinc-400 mb-2">
                  Upgrading: <span className="text-white font-medium">{selectedUser.name || selectedUser.email}</span>
                </p>
                <p className="text-xs text-zinc-500">
                  Current plan: <span className="text-zinc-300">{userPlans[selectedUser.id]?.plan || "ambassador"}</span>
                </p>
              </div>
              
              <div>
                <Label className="text-zinc-300">New Plan</Label>
                <Select
                  value={upgradeForm.plan}
                  onValueChange={(v) => setUpgradeForm({ ...upgradeForm, plan: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="ambassador">Ambassador (Free - Watermarked)</SelectItem>
                    <SelectItem value="apprentice">Apprentice (7,250/mo - No Watermark)</SelectItem>
                    <SelectItem value="director">Director (19,750/mo - No Watermark)</SelectItem>
                    <SelectItem value="studio">Studio (49,750/mo - No Watermark)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label className="text-zinc-300">Reason</Label>
                <Input
                  value={upgradeForm.reason}
                  onChange={(e) => setUpgradeForm({ ...upgradeForm, reason: e.target.value })}
                  placeholder="e.g., Beta tester upgrade"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                />
              </div>
              
              <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-3 text-sm">
                <p className="text-purple-300">
                  <strong>Admin Upgrade:</strong> This is a free upgrade (no credit card required). 
                  The user will receive the plan's monthly sparks immediately and the upgrade is valid for 1 year.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleUpgradeUser} 
              className="bg-purple-600 hover:bg-purple-700"
              disabled={saving}
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Upgrade Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Grant Sparks Dialog */}
      <Dialog open={grantSparksDialogOpen} onOpenChange={setGrantSparksDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-400" />
              Grant Sparks
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                Granting sparks to: <span className="text-white font-medium">{selectedUser.name || selectedUser.email}</span>
              </p>
              <p className="text-xs text-zinc-500">
                Current balance: {userPlans[selectedUser.id]?.spark_balance?.toLocaleString() || 0} sparks
              </p>
              
              <div>
                <Label className="text-zinc-300">Amount to Grant</Label>
                <Input
                  type="number"
                  value={sparkAmount}
                  onChange={(e) => setSparkAmount(parseInt(e.target.value) || 0)}
                  min={1}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="grant-spark-amount"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Reason (optional)</Label>
                <Input
                  value={sparkReason}
                  onChange={(e) => setSparkReason(e.target.value)}
                  placeholder="e.g., Compensation for issue, Beta reward"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="grant-spark-reason"
                />
              </div>
              
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-sm">
                <p className="text-green-300">
                  New balance will be: <strong>{((userPlans[selectedUser.id]?.spark_balance || 0) + sparkAmount).toLocaleString()}</strong> sparks
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleGrantSparks} 
              className="bg-green-600 hover:bg-green-700"
              disabled={saving || sparkAmount <= 0}
              data-testid="confirm-grant-sparks"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Grant {sparkAmount.toLocaleString()} Sparks
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Deduct Sparks Dialog */}
      <Dialog open={deductSparksDialogOpen} onOpenChange={setDeductSparksDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Minus className="w-5 h-5 text-red-400" />
              Deduct Sparks
            </DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-4">
              <p className="text-sm text-zinc-400">
                Deducting sparks from: <span className="text-white font-medium">{selectedUser.name || selectedUser.email}</span>
              </p>
              <p className="text-xs text-zinc-500">
                Current balance: {userPlans[selectedUser.id]?.spark_balance?.toLocaleString() || 0} sparks
              </p>
              
              <div>
                <Label className="text-zinc-300">Amount to Deduct</Label>
                <Input
                  type="number"
                  value={sparkAmount}
                  onChange={(e) => setSparkAmount(Math.min(parseInt(e.target.value) || 0, userPlans[selectedUser.id]?.spark_balance || 0))}
                  min={1}
                  max={userPlans[selectedUser.id]?.spark_balance || 0}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="deduct-spark-amount"
                />
              </div>
              
              <div>
                <Label className="text-zinc-300">Reason (optional)</Label>
                <Input
                  value={sparkReason}
                  onChange={(e) => setSparkReason(e.target.value)}
                  placeholder="e.g., Refund processing, Account adjustment"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="deduct-spark-reason"
                />
              </div>
              
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-sm">
                <p className="text-red-300">
                  New balance will be: <strong>{((userPlans[selectedUser.id]?.spark_balance || 0) - sparkAmount).toLocaleString()}</strong> sparks
                </p>
              </div>
              
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm">
                <p className="text-amber-300">
                  <strong>Note:</strong> If this is for a refund, remember to also process the refund in the Stripe dashboard.
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleDeductSparks} 
              className="bg-red-600 hover:bg-red-700"
              disabled={saving || sparkAmount <= 0}
              data-testid="confirm-deduct-sparks"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Deduct {sparkAmount.toLocaleString()} Sparks
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Billing Details Dialog */}
      <Dialog open={userDetailsDialogOpen} onOpenChange={setUserDetailsDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-indigo-400" />
              User Billing Details
            </DialogTitle>
          </DialogHeader>
          {userBillingDetails && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-500 text-xs">Email</Label>
                  <p className="text-white">{userBillingDetails.email}</p>
                </div>
                <div>
                  <Label className="text-zinc-500 text-xs">Name</Label>
                  <p className="text-white">{userBillingDetails.name || "-"}</p>
                </div>
              </div>
              
              <div className="bg-zinc-800/50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="text-zinc-400">Plan</span>
                  <Badge variant="outline" className={PLAN_DISPLAY[userBillingDetails.subscription_plan]?.color || "border-zinc-500"}>
                    {PLAN_DISPLAY[userBillingDetails.subscription_plan]?.name || userBillingDetails.subscription_plan}
                  </Badge>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Spark Balance</span>
                  <span className="text-white font-medium">{userBillingDetails.spark_balance?.toLocaleString() || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Billing Cycle</span>
                  <span className="text-white">{userBillingDetails.billing_cycle || "N/A"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Subscription Status</span>
                  <Badge variant="outline" className={
                    userBillingDetails.subscription_status === "active" ? "border-green-500/30 text-green-400" :
                    userBillingDetails.subscription_status === "past_due" ? "border-amber-500/30 text-amber-400" :
                    "border-zinc-500/30 text-zinc-400"
                  }>
                    {userBillingDetails.subscription_status || "N/A"}
                  </Badge>
                </div>
                {userBillingDetails.subscription_end_date && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400">
                      {userBillingDetails.cancel_at_period_end ? "Cancels On" : "Renews On"}
                    </span>
                    <span className={userBillingDetails.cancel_at_period_end ? "text-amber-400" : "text-white"}>
                      {new Date(userBillingDetails.subscription_end_date).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {userBillingDetails.stripe_customer_id && (
                  <div className="flex justify-between">
                    <span className="text-zinc-400">Stripe Customer</span>
                    <a 
                      href={`https://dashboard.stripe.com/customers/${userBillingDetails.stripe_customer_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-400 hover:text-indigo-300 text-sm"
                    >
                      View in Stripe →
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Coupon Dialog */}
      <Dialog open={couponDialogOpen} onOpenChange={setCouponDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="w-5 h-5 text-indigo-400" />
              Create Coupon
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Coupon Name *</Label>
                <Input
                  value={couponForm.name}
                  onChange={(e) => setCouponForm({ ...couponForm, name: e.target.value })}
                  placeholder="e.g., Welcome Discount"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="coupon-name"
                />
              </div>
              <div>
                <Label className="text-zinc-300">Promo Code *</Label>
                <Input
                  value={couponForm.code}
                  onChange={(e) => setCouponForm({ ...couponForm, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., WELCOME20"
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="coupon-code"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Discount Type</Label>
                <Select
                  value={couponForm.discount_type}
                  onValueChange={(v) => setCouponForm({ ...couponForm, discount_type: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="percent">Percentage Off</SelectItem>
                    <SelectItem value="amount">Fixed Amount Off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-300">
                  {couponForm.discount_type === "percent" ? "Percent Off" : "Amount Off ($)"}
                </Label>
                <Input
                  type="number"
                  value={couponForm.discount_value}
                  onChange={(e) => setCouponForm({ ...couponForm, discount_value: parseFloat(e.target.value) || 0 })}
                  min={0}
                  max={couponForm.discount_type === "percent" ? 100 : undefined}
                  className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  data-testid="coupon-discount-value"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Duration</Label>
                <Select
                  value={couponForm.duration}
                  onValueChange={(v) => setCouponForm({ ...couponForm, duration: v })}
                >
                  <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="once">Once (first payment only)</SelectItem>
                    <SelectItem value="repeating">Repeating (multiple months)</SelectItem>
                    <SelectItem value="forever">Forever</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {couponForm.duration === "repeating" && (
                <div>
                  <Label className="text-zinc-300">Months</Label>
                  <Input
                    type="number"
                    value={couponForm.duration_in_months}
                    onChange={(e) => setCouponForm({ ...couponForm, duration_in_months: parseInt(e.target.value) || 1 })}
                    min={1}
                    max={12}
                    className="bg-zinc-800 border-zinc-700 text-white mt-1"
                  />
                </div>
              )}
            </div>
            
            <div>
              <Label className="text-zinc-300">Max Redemptions (optional)</Label>
              <Input
                type="number"
                value={couponForm.max_redemptions}
                onChange={(e) => setCouponForm({ ...couponForm, max_redemptions: parseInt(e.target.value) || null })}
                min={1}
                placeholder="Leave empty for unlimited"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
                data-testid="coupon-max-redemptions"
              />
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                Cancel
              </Button>
            </DialogClose>
            <Button 
              onClick={handleCreateCoupon} 
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={saving || !couponForm.name || !couponForm.code}
              data-testid="confirm-create-coupon"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Create Coupon
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
